/**
 * The submit buttons every form goes through — where double-submit
 * protection and save feedback live ONCE, so a form cannot forget them.
 *
 * Two components, two form shapes:
 *
 * - `SubmitButton` — a create form's single ending. Disabled while the
 *   mutation is pending, label says so. That is the whole contract, and it
 *   is structural: the RHF-migrated forms each maintained
 *   `disabled={submitting}` by hand, which is exactly the kind of
 *   per-form convention this module exists to retire.
 * - `SaveButton` — an edit form's ending, which has two more duties.
 *   It is disabled when the form is CLEAN (`dirty === false`): re-saving an
 *   unchanged row is a no-op the server tolerates but the operator never
 *   means. And the save confirmation renders ON the button — the inline
 *   "Saved." line used to appear below the form and push the layout down,
 *   so the acknowledgment of a click moved the thing that was clicked.
 *
 * `SaveButton`'s label sits in a persistent `role="status"` span so the
 * confirmation ("Saved") is announced by assistive tech — a live region
 * only announces CHANGES to content that exists, which is why the span is
 * always there rather than mounted with the confirmation. `SubmitButton`
 * deliberately has no live region: a create form's confirmation is the
 * page it navigates to (or its own inline `role="status"` line), and a
 * second status element per form makes every role query ambiguous.
 *
 * The disabled attribute does not stop Enter in a text field from firing
 * the form's submit handler — every form still guards its `onSubmit`
 * (skip the mutate when clean or pending). These buttons are the visible
 * half of that rule, not a replacement for it.
 */
import { useId, type ReactNode } from 'react'
import { Button, Icon, Spinner, type ButtonProps, type SpinnerProps } from '@speaking-coach/ui'

/**
 * The in-flight spinner every pending submit wears: the shared Spinner in the
 * button's text color, at icon size. Reduced motion parks the rotation; the
 * label still says "Saving…"/"Creating…", so nothing is lost with it parked.
 * Submit buttons pass their variant; other hosts can choose a surface explicitly.
 */
export function PendingSpinner({ variant = 'primary', surface = variant === 'primary' ? 'accent' : 'card' }: { variant?: ButtonProps['variant']; surface?: SpinnerProps['surface'] }): JSX.Element {
  return <Spinner surface={surface} tone="current" size={16} thickness={2} />
}

export interface SubmitButtonProps extends Omit<ButtonProps, 'type'> {
  /** The mutation's `isPending`. Disables the button and swaps the label. */
  pending: boolean
  /** Label while pending, e.g. "Creating…". */
  pendingLabel: ReactNode
}

/** A create form's submit: structurally disabled while the write is in flight. */
export function SubmitButton({
  pending,
  pendingLabel,
  disabled,
  children,
  variant = 'primary',
  ...props
}: SubmitButtonProps): JSX.Element {
  return (
    <Button
      type="submit"
      variant={variant}
      pending={pending}
      disabled={pending || disabled}
      {...props}
    >
      {pending ? (
        <>
          <PendingSpinner variant={variant} />
          {pendingLabel}
        </>
      ) : (
        children
      )}
    </Button>
  )
}

export interface SaveButtonProps extends Omit<ButtonProps, 'type'> {
  /** The mutation's `isPending`. */
  pending: boolean
  /** The form's `formState.isDirty`. A clean form has nothing to save. */
  dirty: boolean
  /**
   * The mutation's `isSuccess`. With a clean form this renders the
   * confirmation ("Saved" + check) in place of the label; it reverts the moment
   * the form dirties again, so no timer is needed and the layout never
   * shifts. Requires the form to `reset(freshRow)` on success — without
   * that `dirty` never clears and the confirmation never shows.
   */
  justSaved: boolean
}

/** An edit form's submit: dirty-gated, with the confirmation on the button. */
export function SaveButton({
  pending,
  dirty,
  justSaved,
  disabled,
  children = 'Save changes',
  variant = 'primary',
  ...props
}: SaveButtonProps): JSX.Element {
  const labelId = useId()
  const label = pending ? (
    <>
      <PendingSpinner variant={variant} />
      Saving…
    </>
  ) : justSaved && !dirty ? (
    <>
      Saved <Icon name="done" />
    </>
  ) : (
    children
  )
  return (
    <Button
      type="submit"
      variant={variant}
      pending={pending}
      disabled={pending || !dirty || disabled}
      aria-labelledby={labelId}
      {...props}
    >
      <span id={labelId} role="status" className="inline-flex items-center gap-1.5">
        {label}
      </span>
    </Button>
  )
}
